import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorScreen } from '@/shared/ui';
import { reportError } from '@/shared/lib/reportError';

/**
 * Не догрузился чанк ленивой страницы или компонента. Почти всегда это
 * деплой: открытая вкладка держит старый `index.html` и просит чанки старой
 * сборки, которых на сервере уже нет. Тексты у браузеров разные — Chrome,
 * Safari, Firefox по порядку.
 */
const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function isChunkLoadError(error: Error): boolean {
  return CHUNK_ERROR.test(error.message);
}

const RELOAD_KEY = 'app:chunk-reload-at';

/**
 * Перезагрузка подтянет новую сборку, но только одна за минуту: если чанка
 * нет и в новой, повторная перезагрузка зациклилась бы — тогда показываем
 * экран ошибки.
 */
function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY));
    if (last && Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Последний рубеж: без него любая ошибка рендера оставляет в Telegram пустой
 * чёрный экран без единой подсказки, что произошло и что делать.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[app] unhandled render error', error, info.componentStack);
    const chunk = isChunkLoadError(error);
    reportError(chunk ? 'chunk' : 'render', error, info.componentStack ?? undefined);
    if (chunk && reloadOnce()) window.location.reload();
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-dvh items-center justify-center">
        <ErrorScreen onRetry={() => window.location.reload()} />
      </div>
    );
  }
}
